import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import SettingsPage from './components/user/SettingsPage';
import AIEnrichmentPage from './components/user/AIEnrichmentPage';
import LeadFinderRouter from './components/peaky-leads/LeadFinderRouter';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import ProtectedUserManagerRoute from './components/ProtectedUserManagerRoute';
import ProtectedPartnerAdminRoute from './components/ProtectedPartnerAdminRoute';
import UserLayout from './components/user/UserLayout';
import Login from './components/Login';
import Register from './components/Register';
import VerificationPending from './components/VerificationPending';
import ResetPassword from './components/ResetPassword';
import EmailVerifyCallback from './components/EmailVerifyCallback';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import UserManagerDashboard from './components/UserManagerDashboard';
import PartnerAdminDashboard from './components/PartnerAdminDashboard';
import PaymentSuccess from './components/user/PaymentSuccess';
import PaymentFailed from './components/user/PaymentFailed';
import PaymentStatus from './components/user/PaymentStatus';
import PaymentError from './components/user/PaymentError';
import { useMetaPixel } from './hooks/useMetaPixel';
import './App.css';

const queryClient = new QueryClient();

function App() {
  // Initialize Meta Pixel based on admin settings
  useMetaPixel();
  
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verification-pending" element={<VerificationPending />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<EmailVerifyCallback />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* User area with persistent sidebar */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <UserLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Dashboard />} />
              {/* Upcoming Features - Dedicated Routes */}
              <Route path="lead-finder" element={<LeadFinderRouter />} />
              <Route path="ocean-io" element={<div style={{ padding: '40px', textAlign: 'center' }}><h2>🌊 Ocean.io</h2><p>Coming Soon</p></div>} />
              <Route path="sales-navigator" element={<div style={{ padding: '40px', textAlign: 'center' }}><h2>💼 Sales Navigator</h2><p>Coming Soon</p></div>} />
              <Route path="instagram" element={<div style={{ padding: '40px', textAlign: 'center' }}><h2>📸 Instagram</h2><p>Coming Soon</p></div>} />
              <Route path="social-watch" element={<div style={{ padding: '40px', textAlign: 'center' }}><h2>👁️ Social Watch</h2><p>Coming Soon</p></div>} />
              <Route path="crunchbase" element={<div style={{ padding: '40px', textAlign: 'center' }}><h2>🏢 Crunchbase</h2><p>Coming Soon</p></div>} />
              <Route path="ai-enrichment" element={<AIEnrichmentPage />} />
              <Route path="settings/profile" element={<SettingsPage />} />
              <Route path="settings/password" element={<SettingsPage />} />
              <Route path="settings/orders" element={<SettingsPage />} />
              <Route path="settings/credit-ledger" element={<SettingsPage />} />
              <Route path="settings/api" element={<SettingsPage />} />
              {/* Legacy orders path, render in-layout */}
              <Route path="orders" element={<SettingsPage />} />
              {/* Payment callback routes (nested under dashboard for sidebar consistency) */}
              <Route path="payment-success" element={<PaymentSuccess />} />
              <Route path="payment-failed" element={<PaymentFailed />} />
              <Route path="payment-status" element={<PaymentStatus />} />
              <Route path="payment-error" element={<PaymentError />} />
            </Route>

            {/* Admin area */}
            <Route path="/admin" element={
              <ProtectedAdminRoute>
                <AdminDashboard />
              </ProtectedAdminRoute>
            }>

              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="users" element={<AdminDashboard />} />
              <Route path="user-approvals" element={<AdminDashboard />} />
              <Route path="subscription-plans" element={<AdminDashboard />} />
              <Route path="subscriptions" element={<AdminDashboard />} />
              <Route path="order-management" element={<AdminDashboard />} />
              <Route path="settings" element={<AdminDashboard />} />
              <Route path="scraper-settings" element={<AdminDashboard />} />
              <Route path="reports" element={<AdminDashboard />} />
              <Route path="partner-wallet" element={<AdminDashboard />} />
              <Route path="partner-wallet-ledger" element={<AdminDashboard />} />
            </Route>

            {/* User Manager area */}
            <Route path="/user-manager" element={
              <ProtectedUserManagerRoute>
                <UserManagerDashboard />
              </ProtectedUserManagerRoute>
            }>
              <Route path="dashboard" element={<UserManagerDashboard />} />
              <Route path="users" element={<UserManagerDashboard />} />
              <Route path="user-approvals" element={<UserManagerDashboard />} />
              <Route path="subscriptions" element={<UserManagerDashboard />} />
              <Route path="reports" element={<UserManagerDashboard />} />
            </Route>

            {/* Partner Admin area */}
            <Route path="/partner-admin" element={
              <ProtectedPartnerAdminRoute>
                <PartnerAdminDashboard />
              </ProtectedPartnerAdminRoute>
            }>
              <Route index element={<Navigate to="/partner-admin/dashboard" replace />} />
              <Route path="dashboard" element={<PartnerAdminDashboard />} />
              <Route path="users" element={<PartnerAdminDashboard />} />
              <Route path="user-approvals" element={<PartnerAdminDashboard />} />
              <Route path="subscriptions" element={<PartnerAdminDashboard />} />
              <Route path="subscription-management" element={<PartnerAdminDashboard />} />
              <Route path="order-management" element={<PartnerAdminDashboard />} />
              <Route path="reports" element={<PartnerAdminDashboard />} />
              <Route path="partner-wallet" element={<PartnerAdminDashboard />} />
              <Route path="partner-wallet-ledger" element={<PartnerAdminDashboard />} />
              <Route path="settings" element={<PartnerAdminDashboard />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;